import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { FormTooltip } from "@/components/ui/form-description";

interface DatePickerFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends React.HTMLAttributes<HTMLDivElement> {
  control: Control<TFieldValues>;
  name: TName;
  label: string;
  description?: string;
  descriptionAsTooltip?: boolean;
  required?: boolean;
  requiredLabel?: string;
  visuallyHiddenLabel?: boolean;
  placeholder?: string;
  disabled?: (date: Date) => boolean;
  startMonth?: Date | undefined;
  endMonth?: Date | undefined;
}

export function DatePickerField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  description,
  descriptionAsTooltip = false,
  required = false,
  requiredLabel = "Required",
  visuallyHiddenLabel = false,
  placeholder = "Pick a date",
  disabled,
  className,
  startMonth,
  endMonth,
  ...htmlProps
}: DatePickerFieldProps<TFieldValues, TName>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn("flex flex-col", className)} {...htmlProps}>
          <div className={cn(visuallyHiddenLabel ? "sr-only" : "")}>
            <FormLabel className="inline-block leading-snug">
              {label}
              {required && (
                <>
                  <span className="ms-1 text-red-500" aria-hidden="true">
                    *
                  </span>
                  <span className="sr-only">({requiredLabel})</span>
                </>
              )}
            </FormLabel>
            {description && !descriptionAsTooltip && (
              <FormDescription>{description}</FormDescription>
            )}
            {description && descriptionAsTooltip && (
              <FormTooltip description={description} />
            )}
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  variant="outline"
                  className={cn(
                    "bg-transparent pl-3 text-left font-normal hover:bg-transparent dark:bg-transparent",
                    !field.value && "text-muted-foreground",
                  )}
                >
                  {field.value ? (
                    format(field.value, "PPP")
                  ) : (
                    <span>{placeholder}</span>
                  )}
                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                </Button>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={field.value}
                onSelect={field.onChange}
                disabled={disabled}
                captionLayout="dropdown"
                startMonth={startMonth}
                endMonth={endMonth}
              />
            </PopoverContent>
          </Popover>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
